'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import { MESSAGE_ORG_SENDER_EMBEDS } from '../lib/org-conversation';
import type {
  MessageOrgAuthor,
  MessageSenderOrganization,
  MessageWithSender,
  UserProfile,
} from '../types';

// Monotonic, process-wide counter appended to realtime channel names. Date.now()
// alone is NOT unique enough: under React Strict Mode (and rapid dependency
// changes) the effect can re-run within the same millisecond, producing an
// identical channel name. Supabase then returns the EXISTING (already-subscribing)
// channel, and registering .on() handlers on it throws "cannot add
// postgres_changes callbacks ... after subscribe()" (Sentry 7511365018). A
// per-invocation counter guarantees a fresh channel every time.
let realtimeChannelSeq = 0;

/**
 * Raw message data from database query
 */
interface MessageQueryRow {
  id: string;
  conversation_id: string;
  sender_id: string | null;
  sender_kind: string;
  sender_organization_id: string | null;
  content: string;
  content_type: string;
  attachments: unknown[] | null;
  reply_to_id: string | null;
  is_edited: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown> | null;
  sender: UserProfile | null;
  sender_organization: MessageSenderOrganization | null;
  org_author: MessageOrgAuthor | null;
  reply_to: (MessageQueryRow & { sender: UserProfile | null }) | null;
}

/**
 * Paginated messages result
 */
interface MessagesPageResult {
  messages: MessageWithSender[];
  nextCursor: string | null;
}

/**
 * Infinite query data structure for messages
 */
interface MessagesInfiniteData {
  pages: MessagesPageResult[];
  pageParams: (string | null)[];
}

const DEFAULT_PAGE_SIZE = 50;

interface UseMessagesOptions {
  conversationId: string | null;
  limit?: number;
  realtime?: boolean;
}

/**
 * Hook to fetch messages with cursor-based pagination and realtime updates
 */
export function useMessages(options: UseMessagesOptions) {
  const {
    conversationId,
    limit = DEFAULT_PAGE_SIZE,
    realtime = true,
  } = options;
  const client = useSupabase();
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof client.channel> | null>(null);
  const currentUserIdRef = useRef<string | null>(null);

  // Fetch current user ID once on mount (use ref to avoid subscription re-creation)
  useEffect(() => {
    client.auth.getUser().then(({ data: { user } }) => {
      currentUserIdRef.current = user?.id ?? null;
    });
  }, [client]);

  const queryKey = useMemo(
    () => ['chat-messages', conversationId],
    [conversationId],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }): Promise<MessagesPageResult> => {
      if (!conversationId) {
        return { messages: [], nextCursor: null };
      }

      let queryBuilder = client
        .from('messages')
        .select(
          `
          *,
          sender:profiles!messages_sender_id_profiles_fkey(id, first_name, last_name, profile_photo_url, is_deleted, deleted_at, auth_user_id, is_merged),
          ${MESSAGE_ORG_SENDER_EMBEDS}
        `,
        )
        .eq('conversation_id', conversationId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(limit + 1);

      // Apply cursor if provided
      if (pageParam) {
        queryBuilder = queryBuilder.lt('created_at', pageParam);
      }

      const { data, error } = await queryBuilder;

      if (error) {
        throw error;
      }

      const rawMessages = (data || []) as unknown as MessageQueryRow[];
      const hasMore = rawMessages.length > limit;

      // Remove the extra message we fetched
      if (hasMore) {
        rawMessages.pop();
      }

      const nextCursor = hasMore
        ? rawMessages[rawMessages.length - 1]?.created_at || null
        : null;

      // Transform to MessageWithSender
      const transformedMessages: MessageWithSender[] = rawMessages.map(
        (msg) =>
          ({
            ...msg,
            // A placeholder for a sender whose profile row could not be read.
            // first_name stays NULL rather than the literal 'Unknown': a fake
            // name defeats resolveIdentity, which can then no longer tell a
            // deleted account from an unclaimed import from an RLS-restricted
            // row, and renders one dead-end word for all three.
            sender: msg.sender ?? null,
            reply_to: msg.reply_to || null,
          }) as MessageWithSender,
      );

      return {
        messages: transformedMessages,
        nextCursor,
      };
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!conversationId,
    staleTime: 30000,
  });

  // Flatten all messages from pages
  const allMessages = useMemo(() => {
    return query.data?.pages.flatMap((page) => page.messages) || [];
  }, [query.data]);

  // Set up realtime subscription for new messages
  useEffect(() => {
    if (!realtime || !conversationId) {
      return;
    }

    // Clean up existing subscription before creating a new one
    if (channelRef.current) {
      client.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // Unique channel name per subscription instance so Supabase never returns an
    // already-subscribed channel when the effect re-runs (Strict Mode double-invoke
    // or dependency changes). Date.now() can collide within a millisecond, so a
    // monotonic counter is appended to guarantee uniqueness — all .on() listeners
    // must register on a fresh channel before .subscribe().
    const channelName = `chat-messages:${conversationId}:${Date.now()}:${++realtimeChannelSeq}`;

    // Subscribe to new messages in this conversation
    const channel = client
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const newMessage = payload.new as MessageWithSender;

          // Skip cache update for own messages - optimistic update already handles this.
          // This prevents the duplicate flash when sending messages.
          // BAL-580: an organization message carries no sender id in the
          // realtime row (by design: the external person receives the same
          // payload). Its org and author come from the refetch, which applies
          // RLS to the author, so never render the raw row.
          if (
            newMessage.sender_kind === 'organization' ||
            (currentUserIdRef.current &&
              newMessage.sender_id === currentUserIdRef.current)
          ) {
            // Only invalidate to get full sender data
            queryClient.invalidateQueries({ queryKey });
            return;
          }

          // For messages from others, add to cache with deduplication
          queryClient.setQueryData<MessagesInfiniteData>(
            queryKey,
            (oldData) => {
              if (!oldData?.pages?.[0]) return oldData;

              // Check if message already exists (deduplication)
              const messageExists = oldData.pages.some((page) =>
                page.messages.some((m) => m.id === newMessage.id),
              );
              if (messageExists) return oldData;

              // Add sender placeholder if not present
              if (!newMessage.sender && newMessage.sender_id) {
                newMessage.sender = {
                  id: newMessage.sender_id,
                  first_name: null,
                  last_name: null,
                  profile_photo_url: null,
                };
              }

              return {
                ...oldData,
                pages: [
                  {
                    ...oldData.pages[0],
                    messages: [newMessage, ...oldData.pages[0].messages],
                  },
                  ...oldData.pages.slice(1),
                ],
              };
            },
          );

          // Refetch to get full message data with sender info
          queryClient.invalidateQueries({ queryKey });
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        client.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [client, conversationId, realtime, queryClient, queryKey]);

  // Helper to add optimistic message
  const addOptimisticMessage = useCallback(
    (message: Partial<MessageWithSender>) => {
      queryClient.setQueryData<MessagesInfiniteData>(queryKey, (oldData) => {
        if (!oldData?.pages?.[0]) return oldData;

        return {
          ...oldData,
          pages: [
            {
              ...oldData.pages[0],
              messages: [
                message as MessageWithSender,
                ...oldData.pages[0].messages,
              ],
            },
            ...oldData.pages.slice(1),
          ],
        };
      });
    },
    [queryClient, queryKey],
  );

  // Helper to remove optimistic message on error
  const removeOptimisticMessage = useCallback(
    (tempId: string) => {
      queryClient.setQueryData<MessagesInfiniteData>(queryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.filter((m) => m.id !== tempId),
          })),
        };
      });
    },
    [queryClient, queryKey],
  );

  return {
    ...query,
    messages: allMessages,
    addOptimisticMessage,
    removeOptimisticMessage,
  };
}

/**
 * Hook to get a single message by ID
 */
export function useMessage(messageId: string | null) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['chat-message', messageId],
    queryFn: async (): Promise<MessageWithSender | null> => {
      if (!messageId) return null;

      const { data, error } = await client
        .from('messages')
        .select(
          `
          *,
          sender:profiles!messages_sender_id_profiles_fkey(id, first_name, last_name, profile_photo_url, is_deleted, deleted_at, auth_user_id, is_merged),
          ${MESSAGE_ORG_SENDER_EMBEDS}
        `,
        )
        .eq('id', messageId)
        .single();

      if (error) {
        throw error;
      }

      const msg = data as unknown as MessageQueryRow;
      return {
        ...msg,
        sender: msg.sender ?? null,
        reply_to: msg.reply_to || null,
      } as MessageWithSender;
    },
    enabled: !!messageId,
    staleTime: 60000,
  });
}
